import React from 'react';
import { Section } from 'shared/ui/Section';
import { Field } from 'shared/ui/Field';
import { NumField, useNumberDraft } from 'shared/ui/NumField';
import { Switch } from 'shared/ui/Switch';
import { LedButton } from 'shared/ui/LedButton';
import { Picker } from 'shared/ui/Picker';
import { useReportSectionStatus } from 'shared/state/sectionStatusContext';
import { useStorageSetSection } from './useStorageSetSection';
import type { StorageVariant } from './storageSetSummary';

type SectionState = ReturnType<typeof useStorageSetSection>;

/** CONF-217: both sizing boxes hold a draft and commit on blur/Enter through
 *  useNumberDraft, so "13" is typeable past a floor of 3 and a keystroke never
 *  re-runs the pipeline. The floor hint ("min 3") takes the Field's hint slot. */
function NodeCountControl({ s, noun }: { s: SectionState; noun: string }) {
  const d = useNumberDraft({ value: s.nodeCount, min: s.minNodes, integer: true, onCommit: s.onNodeCountChange });
  return (
    <Field label={`${noun} Node Count`} hint={d.hint}>
      <NumField ariaLabel={`${noun} node count`} value={d.value} min={s.minNodes}
        onChange={d.onChange} onBlur={d.onBlur} onKeyDown={d.onKeyDown} />
    </Field>
  );
}

function MinRawCapacityControl({ s, noun }: { s: SectionState; noun: string }) {
  // An empty box is a capacity of 0 (the classic default), not a clamp.
  const d = useNumberDraft({ value: s.minRawPb, min: 0, emptyAs: 0, onCommit: s.onMinRawPbChange });
  return (
    <Field label={`${noun} Min RAW Capacity`} hint={d.hint}>
      <NumField ariaLabel={`${noun} minimum raw capacity`} value={d.value} min={0} step="0.1" unit="PB"
        onChange={d.onChange} onBlur={d.onBlur} onKeyDown={d.onKeyDown} />
    </Field>
  );
}

/** Whichever of node-count / min-raw-capacity the system-wide sizing mode
 *  selects. The mode itself is set by SizingModeToggle, in the rail's stat block. */
function SizingControls({ s, noun }: { s: SectionState; noun: string }) {
  return s.sizeBy === 'nodeCount' ? <NodeCountControl s={s} noun={noun} /> : <MinRawCapacityControl s={s} noun={noun} />;
}

/** CONF-218: the body is three fixed rows, each its own grid, so a control only one
 *  card has (Node Model, JBOD, HDD, MLEC) never shifts a shared one — the team found
 *  Encoding at a different place on each card when everything shared one grid. A
 *  row with nothing in it is not drawn (Expansion hides most of two of them). */
function Row({ id, name, children }: { id: string; name: string; children: React.ReactNode }) {
  return <div data-testid={`${id}-row-${name}`} className="grid gap-4 md:grid-cols-3">{children}</div>;
}

/** Row 1 — sizing, the SSD, then the card's own media: the gated Flash Node
 *  Model on Flash, JBOD + HDD on Capacity. Picker ids are prefixed with the section
 *  id (`sectionId`) because Flash and Capacity mount together for every tier —
 *  bare ids like "ssd-options" would collide. */
function MediaRow({ s, flash, noun, sectionId }: { s: SectionState; flash: boolean; noun: string; sectionId: string }) {
  return (
    <Row id={sectionId} name="media">
      <SizingControls s={s} noun={noun} />
      <Picker id={`${sectionId}-ssd`} label="NVMe SSD" ariaLabel={`${noun} NVMe SSD`}
        options={s.ssdOptions.map((m) => ({ value: m.partNumber, label: m.displayName }))}
        value={s.ssdValue} onChange={s.onSsdChange} />

      {flash && s.showNodeModel && (
        // CONF-210/259: dualCpuAccess-only Dual CPU flash node; locked with a reason otherwise.
        <Picker id={`${sectionId}-node-model`} label="Flash Node Model" ariaLabel={`${noun} Node Model`}
          gate="Dual CPU access" hint={s.nodeModelReason ?? '2x PCIe + 1x OCP NICs, higher per-node throughput'}
          options={s.nodeModelOptions} value={s.nodeModelValue}
          onChange={s.onNodeModelChange} disabled={s.nodeModelLocked} />
      )}

      {!flash && (
        <>
          <Picker id={`${sectionId}-jbod`} label="JBOD Configuration" ariaLabel={`${noun} JBOD Configuration`}
            options={s.jbodOptions} value={s.jbodValue} onChange={s.onJbodChange} />
          <Picker id={`${sectionId}-hdd`} label="HDD Capacity" ariaLabel={`${noun} HDD Capacity`}
            options={s.hddOptions.map((m) => ({ value: m.partNumber, label: m.displayName }))}
            value={s.hddValue} onChange={s.onHddChange} />
        </>
      )}
    </Row>
  );
}

/** Row 2 — encoding and spares (hidden on Expansion: `showAdvanced` is false
 *  there), then MLEC (CONF-94; Capacity only, on Base and Expansion alike, as
 *  classic shows it on both cards; locked with mlecRules' reason when the vendor
 *  or JBOD can't support it; R12 clears the flag if the JBOD changes underneath).
 *  When live, the hint states its HDD capacity loss (CONF-220). */
function ProtectionRow({ s, noun, sectionId }: { s: SectionState; noun: string; sectionId: string }) {
  if (!s.showAdvanced && !s.showMlec) return null;
  return (
    <Row id={sectionId} name="protection">
      {s.showAdvanced && (
        <>
          <Picker id={`${sectionId}-encoding`} label="Encoding Scheme" ariaLabel={`${noun} Encoding Scheme`}
            options={s.encodingOptions.map((e) => ({ value: e, label: e }))}
            value={s.encodingValue} onChange={s.onEncodingChange} />
          <Picker id={`${sectionId}-spares`} label="Spares" ariaLabel={`${noun} Spares`}
            options={s.sparesOptions.map((n) => ({ value: String(n), label: String(n) }))}
            value={s.sparesValue} onChange={s.onSparesChange} />
        </>
      )}
      {s.showMlec && (
        <Field label="MLEC" hint={s.mlecReason ?? s.mlecLoss ?? 'Multi-Level Erasure Coding'}>
          <Switch checked={s.mlecOn} onChange={s.onMlecChange} ariaLabel={`${noun} MLEC`} disabled={s.mlecLocked} />
        </Field>
      )}
    </Row>
  );
}

/** Row 3 — compression and E2E. Hidden entirely on Expansion. */
function DataRow({ s, noun, sectionId }: { s: SectionState; noun: string; sectionId: string }) {
  if (!s.showAdvanced) return null;
  return (
    <Row id={sectionId} name="data">
      <Picker id={`${sectionId}-compression`} label="Data Compressibility" ariaLabel={`${noun} Data Compressibility`}
        options={s.compressionOptions.map((r) => ({ value: String(r), label: `${r}:1` }))}
        value={s.compressionValue} onChange={s.onCompressionChange} disabled={s.compressionLocked} />
      <Field label="End-to-End Encryption" hint={s.compressionLocked ? 'Compression is locked to 1:1 while E2E is on.' : undefined}>
        <LedButton on={s.e2e} onToggle={s.onE2eToggle} label={s.e2e ? 'On' : 'Off'} ariaLabel={`${noun} end-to-end encryption`} />
      </Field>
    </Row>
  );
}

export function StorageSetSection({ id, variant }: { id: string; variant: StorageVariant }) {
  const s = useStorageSetSection(variant);
  useReportSectionStatus(id, s.status);
  const flash = variant === 'flash';
  const title = flash ? 'Flash Set' : 'Capacity Set';
  const noun = flash ? 'Flash' : 'Mixed Fleet';

  return (
    <Section.Root id={id} defaultOpen disabled={!s.enabled}>
      <Section.Bar
        title={title}
        role={flash ? 'NVMe flash nodes (F)' : 'Hybrid nodes + JBODs (S)'}
        summary={s.isCalculating ? '…' : s.summary}
        trailing={<Switch checked={s.enabled} onChange={s.onToggleEnabled} ariaLabel={`${title} enabled`} offLabel="Off" onLabel="On" />}
      />
      <Section.Body>
        {s.constraintNote && (
          <p role="note" className="mb-3 rounded-[var(--radius-2)] border border-brand/30 bg-brand/10 px-3 py-2 font-body text-xs text-brand-strong">
            {s.constraintNote}
          </p>
        )}
        <div className="flex flex-col gap-5">
          <MediaRow s={s} flash={flash} noun={noun} sectionId={id} />
          <ProtectionRow s={s} noun={noun} sectionId={id} />
          <DataRow s={s} noun={noun} sectionId={id} />
        </div>
      </Section.Body>
    </Section.Root>
  );
}
